import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Languages, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AppSelector = () => {
  const navigate = useNavigate();

  const apps = [
    {
      id: 'pdf-viewer',
      title: 'PDF Viewer',
      description: 'Modern document reading experience with advanced viewing controls and seamless file management.',
      icon: FileText,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      features: ['Flexible Viewing', 'Zoom Controls', 'File Management', 'Theme Options'],
      route: '/pdf',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.palru.pdfviewer'
    },
    {
      id: 'quick-translator',
      title: 'Quick Translator',
      description: 'Break language barriers with AI-powered translation, voice input, and camera OCR technology.',
      icon: Languages,
      gradient: 'from-purple-500 to-pink-600',
      bgGradient: 'from-purple-50 to-pink-50',
      features: ['Smart Translation', 'Voice Input', 'Camera OCR', 'Text-to-Speech'],
      route: '/translator',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.suuin.quicktranslator'
    },
    {
      id: 'tomo',
      title: 'Tomo',
      description: 'Smart task management with dynamic categories, instant search, and history archive.',
      icon: FileText,
      gradient: 'from-orange-500 to-rose-600',
      bgGradient: 'from-amber-50 to-rose-50',
      features: ['Smart Creation', 'Dynamic Categories', 'Real-time Search', 'History Archive'],
      route: '/tomo',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.obrez.daily.dolist'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <div className="flex-1 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Prasadji Games Studio
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose your application to explore powerful mobile solutions
            </p>
          </motion.div>

          {/* App Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {apps.map((app, index) => (
              <motion.div
                key={app.id}
                className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${app.bgGradient} p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                onClick={() => navigate(app.route)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                  <app.icon className="w-full h-full" />
                </div>

                {/* Content */}
                <div className="relative">
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${app.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <app.icon className="w-8 h-8" />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {app.title}
                  </h2>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {app.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {app.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-gradient-to-r bg-blue-500 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(app.route);
                      }}
                      className={`flex-1 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r ${app.gradient} text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 group-hover:scale-105`}
                    >
                      View Website
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                    <a
                      href={app.playStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      Download
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
      
      {/* Footer */}
      <motion.div 
        className="py-8 text-center bg-white/50 backdrop-blur-sm border-t border-gray-200/50"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="text-gray-500 text-sm">
          © 2025 Prasadji Games Studio. All rights reserved.
        </div>
      </motion.div>
    </div>
  );
};

export default AppSelector; 